// Searchable provider picker for the credentialing signing handoff.
// Candidates: org providers (excluding self and providers denied for the same
// payer group), narrowed to the visit location when one is set and matches.

import { useState, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';
import { fetchDeniedProviderIds } from '@/hooks/useCredentialingDeny';

export function AssignedSignerSelect({ value, onChange, locationId, payerGroupId, defaultSupervisorId, clinicalSpecialty }) {
  const { user } = useUser();
  const [providers, setProviders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [users, deniedIds] = await Promise.all([
          apiService.getOrganizationUsers(user?.organization_id || user?.organization?.id),
          fetchDeniedProviderIds(payerGroupId),
        ]);
        if (cancelled) return;
        let list = (users || []).filter(u => u.is_provider && u.id !== user?.id && !deniedIds.has(u.id));
        // Specialty guard: prefer same-specialty signers, fall back when none match
        if (clinicalSpecialty) {
          const sameSpecialty = list.filter(u => u.clinical_specialty === clinicalSpecialty);
          if (sameSpecialty.length > 0) list = sameSpecialty;
        }
        if (locationId) {
          const atLocation = list.filter(u => u.schedulable_location_ids?.includes(locationId));
          if (atLocation.length > 0) list = atLocation;
        }
        setProviders(list);
      } catch (err) {
        console.error('[AssignedSignerSelect] Failed to load providers:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id, locationId, payerGroupId, clinicalSpecialty]);

  // Default to the supervisor once candidates load, if nothing is selected yet
  useEffect(() => {
    if (!value && defaultSupervisorId && providers.some(p => p.id === defaultSupervisorId)) {
      onChange(defaultSupervisorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSupervisorId, providers]);

  const selected = providers.find(p => p.id === value) || null;
  const filtered = providers.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
      >
        {selected ? (
          <span className="font-medium text-gray-900">
            {selected.first_name} {selected.last_name}
            {selected.provider_designation && <span className="text-xs text-gray-500 ml-1">({selected.provider_designation})</span>}
          </span>
        ) : (
          <span className="text-gray-400">Select signer...</span>
        )}
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search providers..."
                className="w-full border border-gray-300 rounded pl-7 pr-2 py-1 text-sm focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No eligible providers found</p>
          ) : (
            filtered.slice(0, 20).map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.id); setIsOpen(false); setSearchTerm(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${p.id === value ? 'bg-blue-50' : ''}`}
              >
                {p.first_name} {p.last_name}
                {p.provider_designation && <span className="text-xs text-gray-500 ml-1">({p.provider_designation})</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
